import React, { useEffect, useState } from 'react'
import { Button, ConfigProvider, Dropdown, Menu, message, Pagination, Table, Tag, Switch, Modal } from 'antd';
import { Wrapper } from '../style';
import moment from 'moment';
import { ConvertParams, defaultPageSize } from '../../../../config';
import { MakeApiCall } from '../../../../apis';


function Settings({userData, setIsModalOpen, setEditing, exposeFetcher}) {

  const [totalPage, setTotalPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [updatingIds, setUpdatingIds] = useState(new Set());

  const [filters, setFilters] = useState({
    page: 1,
    'per-page': defaultPageSize,
    search_text: '',
  });


  const fetchAlertSettingList = async () => {
    setLoading(true);
    try {
      const response = await MakeApiCall(
          `get-alerts${ConvertParams(filters)}`,
          'get',
          null,
          true,
          {},
          userData?.token
      );

      if (response?.status) {
        const records = response?.data?.records || [];
        const pagination = response?.data?.pagination || {};
        setList(records);
        setTotalPage(pagination.totalCount || 0);
      } else {
        message.warning(response?.message || "Failed to load alert settings");
      }
    } catch (error) {
      message.error('Error fetching alert settings.');
      console.error('API Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.token) {
      fetchAlertSettingList();
    }
  }, [filters, userData?.token]);

  useEffect(() => {
    if (exposeFetcher) {
      exposeFetcher(() => fetchAlertSettingList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exposeFetcher]);

  // Handler to update filters on pagination change - This is the ONLY place filters should be updated.
  const handlePaginationChange = (currentPage, newPageSize) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page: currentPage,
      'per-page': newPageSize,
    }));
  };

  // Format timestamp using moment
  const formatTimestamp = (timestamp) => {
    // Check if timestamp is valid (and not 0 or null)
    if (!timestamp) return '-';
    return moment.unix(timestamp).format("YYYY-MM-DD HH:mm:ss");
  };

  // Table columns (rest of the column definitions remain the same)
  const columns = [
    { title: "ID", dataIndex: "id", key: "id" },
    { title: "Name", dataIndex: "name", key: "name", render: (text) => <div className='fw-bold'>{text}</div> },
    { title: "Category", dataIndex: "category", key: "category" },
    { title: "Type", dataIndex: "type", key: "type" },
    { title: "Severity", dataIndex: "severity", key: "severity" },
    { title: "Frequency", dataIndex: "frequency", key: "frequency" },
    {
      title: "Sent On",
      dataIndex: "receive_on",
      key: "receive_on",
      render: (text) => {
        let items = [];
        let rawText = text;
        try {
          while (typeof rawText === 'string') {
            rawText = JSON.parse(rawText);
          }
          if (Array.isArray(rawText)) {
            items = rawText.sort((a, b) => a.localeCompare(b));
          }
        } catch (e) {
          console.error('Failed to parse receive_on:', text, e);
        }

        return items.map((item) => {
          let color = 'blue'; // default
          if (item.toLowerCase() === 'whatsapp') color = 'green';
          else if (item.toLowerCase() === 'email') color = 'volcano';
          else if (item.toLowerCase() === 'all alerts') color = 'purple';

          return <Tag key={item} color={color}>{item}</Tag>;
        });
      }
    },
    {
      title: "ASIN List",
      dataIndex: "asin_list",
      key: "asin_list",
      render: (text) => {
        let items = [];
        try {
          // Added a check for null/undefined text before parsing
          if(text) {
            items = JSON.parse(text);
          }
        } catch (e) {
          return <Tag>N/A</Tag>;
        }
        return items.map((item) => <Tag key={item}>{item}</Tag>);
      },
    },

    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (timestamp) => formatTimestamp(timestamp),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, row) => {
        const isActive = row?.status === 0;
        const updating = updatingIds.has(row.id);
        return (
          <div className="d-flex align-items-center gap-2">
            <Switch
              checked={isActive}
              loading={updating}
              onChange={() => handleToggleStatus(row)}
            />
            <span className="fs-8">{isActive ? 'Active' : 'Inactive'}</span>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, row) => (
          <Dropdown overlay={menu(row)} trigger={["click"]}>
            <Button type="link" className="" data-kt-menu-trigger="click" data-kt-menu-placement="bottom-end" data-kt-menu-overflow="true" ><i className="ki-solid ki-dots-vertical fs-1 text-gray-800 "></i></Button>
          </Dropdown>
      ),
    },
  ];


  const menu = (row) => (
      <Menu className=" min-w-200px p-3">
        <Menu.Item key="1" className="d-flex align-items-center mb-2" onClick={() => handleEdit(row)}><i className="ki-outline ki-pencil fs-3 me-2"></i>Edit Alert</Menu.Item>
        <Menu.Item key="2" className="d-flex align-items-center mb-2" onClick={() => handleDelete(row)}><i className="ki-outline ki-trash fs-3 me-2"></i>Delete Alert</Menu.Item>
      </Menu>
  );

  const handleEdit = (value) => {
    setEditing(value);
    setIsModalOpen(true);
  };

  // Toggle status inline and call API
  const handleToggleStatus = async (row) => {
    if (!row || !row.id) return;
    const newStatus = row.status === 1 ? 0 : 1;

    // mark updating
    setUpdatingIds(prev => new Set(prev).add(row.id));
    try {
      const resp = await MakeApiCall(`update-alert/${row.id}`, 'post', { status: newStatus }, true, {}, userData?.token);
      if (resp?.status) {
        // update local list
        setList(prev => prev.map(item => item.id === row.id ? { ...item, status: newStatus } : item));
        message.success('Alert status updated');
      } else {
        message.error(resp?.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating alert status:', err);
      message.error('Error updating alert status');
    } finally {
      setUpdatingIds(prev => {
        const s = new Set(prev);
        s.delete(row.id);
        return s;
      });
    }
  };

  // Delete handler with confirmation
  const handleDelete = (row) => {
    if (!row || !row.id) return;
    Modal.confirm({
      title: 'Delete Alert',
      content: 'Are you sure you want to delete this alert? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setUpdatingIds(prev => new Set(prev).add(row.id));
        try {
          // Use POST like other alert APIs; change to 'delete' if your backend expects DELETE
          const resp = await MakeApiCall(`delete-alert/${row.id}`, 'delete', null, true, {}, userData?.token);
          if (resp?.status) {
            setList(prev => prev.filter(item => item.id !== row.id));
            message.success('Alert deleted');
          } else {
            message.error(resp?.message || 'Failed to delete alert');
          }
        } catch (err) {
          console.error('Error deleting alert:', err);
          message.error('Error deleting alert');
        } finally {
          setUpdatingIds(prev => {
            const s = new Set(prev);
            s.delete(row.id);
            return s;
          });
        }
      }
    });
  };

  const theme = {
    token: {
      itemActiveBg: '#ffffff',
      itemActiveBgDisabled: 'rgba(0, 0, 0, 0.15)',
      itemActiveColorDisabled: 'rgba(0, 0, 0, 0.25)',
      itemBg: '#ffffff',
      itemInputBg: '#ffffff',
      itemLinkBg: '#ffffff',
      itemSize: 32,
      itemSizeSM: 24,
      miniOptionsSizeChangerTop: 0,
      colorBgContainer: '#ffffff',
      colorBgContainerDisabled: 'rgba(0, 0, 0, 0.04)',
      colorBgTextActive: 'rgb(255, 255, 255)',
      colorBgTextHover: 'rgb(255, 255, 255)',
      colorBorder: '#d9d9d9',
      colorPrimary: '#f45b68',
      colorPrimaryBorder: '#f45b68',
      colorPrimaryHover: '#f45b68',
      colorText: 'rgba(0, 0, 0, 0.88)',
      colorTextDisabled: 'rgba(0, 0, 0, 0.25)',
      colorTextPlaceholder: 'rgba(0, 0, 0, 0.25)',

    },
  };

  return (
      <Wrapper>
        {/*begin::Row*/}
        <div className="row gy-5 g-xl-5">
          {/*begin::Col*/}
          <div className="col-xxl-12">
            {/*begin::Tables Widget 9*/}
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              {/*begin::Header*/}
              <div className="card-header border-bottom-dashed">
                <h3 className="card-title fw-bolder">Alerts List</h3>
                <div className="card-toolbar">

                </div>
              </div>
              {/*end::Header*/}
              {/*begin::Body*/}
              <div className="card-body py-2">
                {/*begin::Table container*/}
                <div className="table-responsive">
                  {/*begin::Table*/}
                  <Table
                      scroll={{ x: "max-content" }}
                      columns={columns}
                      loading={loading}
                      dataSource={list}
                      rowKey="id"
                      pagination={false}
                      components={{
                        table: (props) => (
                            <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...props} />
                        ),
                        header: {
                          wrapper: (props) => <thead {...props} className="" />,
                          row: (props) => <tr {...props} className="fw-bolder text-dark bg-cus" />,
                          cell: (props) => (
                              <th {...props} className={``} />
                          ),
                        },
                      }}
                  />
                  {/*end::Table*/}
                </div>
                {/*end::Table container*/}

              </div>
              {/*begin::Body*/}
            </div>
            {/*end::Tables Widget 9*/}
          </div>
          {/*end::Col*/}
        </div>
        {/*end::Row*/}
        {/*begin::Row*/}
        <div className="row gy-5 g-xl-5">
          {/*begin::Col*/}
          <div className="col-xxl-12">
            {/*begin::Tables Widget 9*/}
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              {/*begin::Body*/}
              <div className="card-body py-2">

                {/* Pagination directly below the table */}
                <ConfigProvider theme={theme}>
                  <Pagination
                      className="mt-3"
                      responsive
                      current={filters.page}
                      pageSize={filters["per-page"]}
                      total={totalPage}
                      showSizeChanger
                      onChange={handlePaginationChange}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                  />
                </ConfigProvider>


              </div>
              {/*begin::Body*/}
            </div>
            {/*end::Tables Widget 9*/}
          </div>
          {/*end::Col*/}
        </div>
        {/*end::Row*/}
      </Wrapper>
  )
}

export default Settings;

